// Header component with integrated mesh controls
import { useState, useRef, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { ControlModal } from './ControlModal';
import { LegendModal } from './LegendModal';
import { Logo } from './Logo';
import { HeaderStats } from './HeaderStats';
import type { MeshControls } from '../types';
import { trackPlaygroundMode, trackExternalLink } from '../utils/analytics';

interface HeaderProps {
  controls: MeshControls;
  onControlsChange: (controls: Partial<MeshControls>) => void;
  onRegenerate?: () => void;
  mesh?: { nodes: any[]; edges: any[] };
  activePackets?: number;
}

export function Header({ controls, onControlsChange, onRegenerate, mesh, activePackets }: HeaderProps) {
  const location = useLocation();
  const isHomePage = location.pathname === '/';
  const [isControlsOpen, setIsControlsOpen] = useState(false);
  const [isLegendOpen, setIsLegendOpen] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const controlsTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const legendTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const controlsRef = useRef<HTMLDivElement>(null);
  const legendRef = useRef<HTMLDivElement>(null);

  const handleControlsMouseEnter = () => {
    if (controlsTimeoutRef.current) {
      clearTimeout(controlsTimeoutRef.current);
      controlsTimeoutRef.current = null;
    }
    setIsControlsOpen(true);
  };

  const handleControlsMouseLeave = () => {
    controlsTimeoutRef.current = setTimeout(() => {
      setIsControlsOpen(false);
    }, 200);
  };

  const handleLegendMouseEnter = () => {
    if (legendTimeoutRef.current) {
      clearTimeout(legendTimeoutRef.current);
      legendTimeoutRef.current = null;
    }
    setIsLegendOpen(true);
  };

  const handleLegendMouseLeave = () => {
    legendTimeoutRef.current = setTimeout(() => {
      setIsLegendOpen(false);
    }, 200);
  };

  useEffect(() => {
    return () => {
      if (controlsTimeoutRef.current) {
        clearTimeout(controlsTimeoutRef.current);
      }
      if (legendTimeoutRef.current) {
        clearTimeout(legendTimeoutRef.current);
      }
    };
  }, []);

  return (
    <header className="fixed top-0 left-0 right-0 z-50 bg-zinc-950/80 backdrop-blur-md border-b border-zinc-800/60 pointer-events-none">
      <div className="mx-auto max-w-7xl px-4 md:px-6 py-2 md:py-3 flex items-center justify-between pointer-events-auto relative z-[60]">
        {/* Logo */}
        <Link 
          to="/" 
          className="flex items-center gap-2 md:gap-3 hover:opacity-80 transition-opacity"
          onClick={() => {
            window.scrollTo({ top: 0, behavior: 'smooth' });
          }}
        >
          <Logo className="w-8 h-8 md:w-10 md:h-10" />
          <div className="flex flex-col">
            <span className="tracking-tight font-semibold text-white leading-none text-sm md:text-base">open-me.sh</span>
            <span className="text-[9px] md:text-[10px] text-zinc-500 leading-none mt-0.5 hidden sm:inline">Interactive Mesh Education</span>
          </div>
        </Link>

        {/* Navigation & Controls */}
        <div className="flex items-center gap-2 md:gap-4">
          {/* Hamburger Menu Button - Mobile Only */}
          {!controls.playgroundMode && (
            <button
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
              className="md:hidden p-2 text-zinc-300 hover:text-white transition-colors"
              aria-label="Toggle mobile menu"
              aria-expanded={isMobileMenuOpen}
            >
              {isMobileMenuOpen ? (
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              ) : (
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
                </svg>
              )}
            </button>
          )}

          {/* Navigation Links - Desktop Only, Hidden in playground mode */}
          {!controls.playgroundMode && (
            <nav className="hidden md:flex items-center gap-6 text-sm text-zinc-300">
              <Link 
                className={`hover:text-white transition ${isHomePage ? 'text-white border-b-2 border-cyan-400' : ''}`}
                to="/"
              >
                Home
              </Link>
              {isHomePage && (
                <>
                  <a className="hover:text-white transition" href="#about">
                    About
                  </a>
                  <a className="hover:text-white transition" href="#playground">
                    Simulator
                  </a>
                </>
              )}
              <Link 
                className={`hover:text-white transition ${location.pathname === '/community-resources' ? 'text-white border-b-2 border-cyan-400' : ''}`}
                to="/community-resources"
              >
                Communities
              </Link>
              <Link 
                className={`hover:text-white transition ${location.pathname === '/mesh-tools' ? 'text-white border-b-2 border-cyan-400' : ''}`}
                to="/mesh-tools"
              >
                Tools
              </Link>
              <a
                className="hover:text-white transition"
                href="https://meshtastic.org/"
                target="_blank"
                rel="noreferrer"
                onClick={() => trackExternalLink('meshtastic.org', 'header_nav')}
              >
                Meshtastic
              </a>
            </nav>
          )}

          {/* Playground Mode Toggle */}
          <button
            onClick={() => {
              const newPlaygroundMode = !controls.playgroundMode;
              onControlsChange({ playgroundMode: newPlaygroundMode });
              setIsMobileMenuOpen(false);
              trackPlaygroundMode(newPlaygroundMode ? 'enter' : 'exit');
            }}
            className={`px-3 md:px-4 py-2 border rounded-full text-xs md:text-sm transition-all backdrop-blur-sm flex items-center gap-1.5 md:gap-2 ${
              controls.playgroundMode
                ? 'bg-cyan-500/20 border-cyan-500/50 text-cyan-400 hover:bg-cyan-500/30'
                : 'bg-zinc-900/90 border-zinc-800 text-zinc-300 hover:text-white hover:border-cyan-400/50'
            }`}
            aria-label="Toggle Playground Mode"
            title={controls.playgroundMode ? 'Show full content' : 'Mesh playground mode'}
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              {controls.playgroundMode ? (
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
              ) : (
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.88 9.88l-3.29-3.29m7.532 7.532l3.29 3.29M3 3l3.59 3.59m0 0A9.953 9.953 0 0112 5c4.478 0 8.268 2.943 9.543 7a10.025 10.025 0 01-4.132 5.411m0 0L21 21" />
              )}
            </svg>
            {controls.playgroundMode ? 'Show Content' : 'Playground'}
          </button>

          {/* Legend Button */}
          <div
            ref={legendRef}
            onMouseEnter={handleLegendMouseEnter}
            onMouseLeave={handleLegendMouseLeave}
            className="relative"
          >
            <button
              className="px-3 md:px-4 py-2 bg-zinc-900/90 border border-zinc-800 rounded-full text-xs md:text-sm text-zinc-300 hover:text-white hover:border-cyan-400/50 transition-all backdrop-blur-sm flex items-center gap-1.5 md:gap-2"
              aria-label="Message Type Legend"
              aria-expanded={isLegendOpen}
            >
              <svg
                className="w-4 h-4"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M7 21a4 4 0 01-4-4V5a2 2 0 012-2h4a2 2 0 012 2v12a4 4 0 01-4 4zm0 0h12a2 2 0 002-2v-4a2 2 0 00-2-2h-2.343M11 7.343l1.657-1.657a2 2 0 012.828 0l2.829 2.829a2 2 0 010 2.828l-8.486 8.485M7 17h.01"
                />
              </svg>
              <span className="hidden sm:inline">Legend</span>
            </button>

            {/* Expandable Modal */}
            <div
              onMouseEnter={handleLegendMouseEnter}
              onMouseLeave={handleLegendMouseLeave}
              className={`absolute top-full right-0 mt-2 w-[90vw] max-w-xs bg-zinc-900/95 border border-zinc-800 rounded-2xl p-5 backdrop-blur-md shadow-xl transition-all duration-200 z-[60] ${
                isLegendOpen
                  ? 'opacity-100 translate-y-0 pointer-events-auto'
                  : 'opacity-0 -translate-y-2 pointer-events-none'
              }`}
            >
              <LegendModal />
            </div>
          </div>

          {/* Mesh Controls Button */}
          <div
            ref={controlsRef}
            onMouseEnter={handleControlsMouseEnter}
            onMouseLeave={handleControlsMouseLeave}
            className="relative"
          >
            <button
              className="px-3 md:px-4 py-2 bg-zinc-900/90 border border-zinc-800 rounded-full text-xs md:text-sm text-zinc-300 hover:text-white hover:border-cyan-400/50 transition-all backdrop-blur-sm flex items-center gap-1.5 md:gap-2"
              aria-label="Mesh Controls"
              aria-expanded={isControlsOpen}
            >
              <svg
                className="w-4 h-4"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M12 6V4m0 2a2 2 0 100 4m0-4a2 2 0 110 4m-6 8a2 2 0 100-4m0 4a2 2 0 110-4m0 4v2m0-6V4m6 6v10m6-2a2 2 0 100-4m0 4a2 2 0 110-4m0 4v2m0-6V4"
                />
              </svg>
              <span className="hidden sm:inline">Controls</span>
            </button>

            {/* Expandable Modal */}
            <div
              onMouseEnter={handleControlsMouseEnter}
              onMouseLeave={handleControlsMouseLeave}
              className={`absolute top-full right-0 mt-2 w-[90vw] max-w-md max-h-[calc(100vh-100px)] bg-zinc-900/95 border border-zinc-800 rounded-2xl backdrop-blur-md shadow-xl transition-all duration-200 z-[60] ${
                isControlsOpen
                  ? 'opacity-100 translate-y-0 pointer-events-auto'
                  : 'opacity-0 -translate-y-2 pointer-events-none'
              }`}
              style={{ scrollbarWidth: 'thin', scrollbarColor: '#52525b #27272a', overflow: 'visible' }}
            >
              <div className="overflow-y-auto overflow-x-visible max-h-[calc(100vh-100px)] p-5">
                <ControlModal controls={controls} onControlsChange={onControlsChange} onRegenerate={onRegenerate} />
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Mobile Menu Overlay */}
      {!controls.playgroundMode && (
        <div
          className={`md:hidden absolute top-full left-0 right-0 bg-zinc-950/95 backdrop-blur-md border-b border-zinc-800/60 transition-all duration-300 ease-in-out overflow-hidden ${
            isMobileMenuOpen
              ? 'max-h-96 opacity-100'
              : 'max-h-0 opacity-0'
          }`}
        >
          <nav className="flex flex-col px-6 py-4 space-y-4 text-base text-zinc-300">
            <Link
              className={`hover:text-white transition py-2 border-b border-zinc-800/50 ${isHomePage ? 'text-white border-l-4 border-l-cyan-400 pl-3 -ml-3' : ''}`}
              to="/"
              onClick={() => setIsMobileMenuOpen(false)}
            >
              Home
            </Link>
            {isHomePage && (
              <>
                <a
                  className="hover:text-white transition py-2 border-b border-zinc-800/50"
                  href="#about"
                  onClick={() => setIsMobileMenuOpen(false)}
                >
                  About
                </a>
                <a
                  className="hover:text-white transition py-2 border-b border-zinc-800/50"
                  href="#playground"
                  onClick={() => setIsMobileMenuOpen(false)}
                >
                  Simulator
                </a>
              </>
            )}
            <Link
              className={`hover:text-white transition py-2 border-b border-zinc-800/50 ${location.pathname === '/community-resources' ? 'text-white border-l-4 border-l-cyan-400 pl-3 -ml-3' : ''}`}
              to="/community-resources"
              onClick={() => setIsMobileMenuOpen(false)}
            >
              Communities
            </Link>
            <Link
              className={`hover:text-white transition py-2 border-b border-zinc-800/50 ${location.pathname === '/mesh-tools' ? 'text-white border-l-4 border-l-cyan-400 pl-3 -ml-3' : ''}`}
              to="/mesh-tools"
              onClick={() => setIsMobileMenuOpen(false)}
            >
              Tools
            </Link>
            <a
              className="hover:text-white transition py-2"
              href="https://meshtastic.org/"
              target="_blank"
              rel="noreferrer"
              onClick={() => {
                setIsMobileMenuOpen(false);
                trackExternalLink('meshtastic.org', 'mobile_nav');
              }}
            >
              Meshtastic
            </a>
          </nav>
        </div>
      )}
      
      {/* Header-embedded stats (only in normal mode) - with slide transition */}
      {mesh && (
        <div 
          className="transition-all duration-800"
          style={{ 
            maxHeight: controls.playgroundMode ? '0' : '200px',
            opacity: controls.playgroundMode ? 0 : 1,
            pointerEvents: controls.playgroundMode ? 'none' : 'auto',
            overflow: controls.playgroundMode ? 'hidden' : 'visible'
          }}
        >
          <HeaderStats 
            controls={controls} 
            mesh={mesh} 
            activePackets={activePackets || 0}
          />
        </div>
      )}
    </header>
  );
}